import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { getStoreProductImageUrls } from '@/lib/storeProductImages';

const INTERVAL_MS = 3000;
const FADE_DURATION = 0.55;

/**
 * One image: static. Two images: crossfade, auto-advance every 3s.
 */
export default function StoreProductImageCarousel({
  product,
  alt,
  className = '',
  heightClass = 'h-48',
  /** e.g. rounded-t-lg for store cards, rounded-lg for modals */
  roundedClassName = 'rounded-t-lg',
}) {
  const urls = getStoreProductImageUrls(product);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    setIdx(0);
  }, [urls[0], urls[1]]);

  useEffect(() => {
    if (urls.length < 2) return;
    const t = setInterval(() => {
      setIdx((i) => (i + 1) % urls.length);
    }, INTERVAL_MS);
    return () => clearInterval(t);
  }, [urls.length]);

  if (urls.length === 0) return null;

  if (urls.length === 1) {
    return (
      <div className={`relative w-full overflow-hidden bg-gray-700 ${roundedClassName} ${heightClass} ${className}`}>
        <img
          src={urls[0]}
          alt={alt}
          className="h-full w-full object-cover"
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
      </div>
    );
  }

  return (
    <div
      className={`relative w-full overflow-hidden bg-gray-700 ${roundedClassName} ${heightClass} ${className}`}
      role="img"
      aria-label={`${alt} — image ${idx + 1} of ${urls.length}`}
    >
      {urls.map((url, i) => (
        <motion.div
          key={url}
          className="absolute inset-0"
          initial={false}
          animate={{
            opacity: i === idx ? 1 : 0,
            zIndex: i === idx ? 1 : 0,
          }}
          transition={{ duration: FADE_DURATION, ease: 'easeInOut' }}
        >
          <img
            src={url}
            alt={i === idx ? alt : ''}
            className="h-full w-full object-cover"
            loading={i === 0 ? 'eager' : 'lazy'}
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        </motion.div>
      ))}
      <div className="absolute bottom-2 left-0 right-0 z-10 flex justify-center gap-1.5 pointer-events-none">
        {urls.map((_, i) => (
          <span
            key={i}
            className={`h-1.5 w-1.5 rounded-full transition-colors ${
              i === idx ? 'bg-white shadow-sm' : 'bg-white/40'
            }`}
          />
        ))}
      </div>
    </div>
  );
}
